import { useMemo } from "react";
import { Navigate, useLocation, Outlet } from "react-router-dom";
import { useMenuNavigationProgram } from "@repo/ui";
import {
  ROUTESWEB,
  getFirstVisibleWebBottomNavTab,
  getMenuNavigationFlags,
  WEB_BOTTOM_NAV_ROUTE_PATHS,
} from "@repo/utils";
import { useIsAuthenticated, useAuthStore } from "../../store/useAuthStore";
import {
  buildGeniusDeeplinkPath,
  isGeniusDeeplink,
  isGeniusDeeplinkEntryPath,
  resolveGeniusResumePath,
} from "../deeplink/genius/parseGeniusDeeplink";
import {
  abandonGeniusDeeplinkIfAuthEscape,
  isGeniusAuthEscapePath,
  isGeniusShellResumePending,
  markGeniusShellResumeInThisTab,
  readPersistedGeniusDeeplinkParams,
  readPersistedGeniusOriginalUrl,
  shouldAutoResumeGeniusDeeplink,
  shouldRedirectBaseUrlToGeniusDeeplink,
} from "../deeplink/genius/geniusDeeplinkSession";

interface AuthGuardProps {
  requireAuth?: boolean;
}

function resolvePendingGeniusResumePath(): string | null {
  if (!shouldRedirectBaseUrlToGeniusDeeplink()) return null;

  const profilesData = useAuthStore.getState().profilesData;
  if (
    !shouldAutoResumeGeniusDeeplink(profilesData) &&
    !isGeniusShellResumePending()
  ) {
    return null;
  }

  return resolveGeniusResumePath(
    readPersistedGeniusOriginalUrl(),
    readPersistedGeniusDeeplinkParams(),
  );
}

export const AuthGuard = ({ requireAuth = true }: AuthGuardProps) => {
  const isAuthenticated = useIsAuthenticated();
  const location = useLocation();
  const { gradeId, examIds } = useMenuNavigationProgram();
  const flags = useMemo(
    () => getMenuNavigationFlags(gradeId, examIds),
    [gradeId, examIds],
  );
  const defaultSignedInPath =
    WEB_BOTTOM_NAV_ROUTE_PATHS[getFirstVisibleWebBottomNavTab(flags)];

  const onGeniusDeeplinkRoute = isGeniusDeeplinkEntryPath(location.pathname);
  const onAuthEscape = isGeniusAuthEscapePath(location.pathname);

  // Login/intro/signup must never be blocked by a sticky Genius session.
  if (onAuthEscape) {
    abandonGeniusDeeplinkIfAuthEscape(location.pathname);
  }

  if (requireAuth && isAuthenticated && !onGeniusDeeplinkRoute) {
    if (isGeniusDeeplink(window.location.href)) {
      const resumePath =
        resolveGeniusResumePath(
          window.location.href,
          readPersistedGeniusDeeplinkParams(),
        ) ??
        buildGeniusDeeplinkPath(
          window.location.search ||
            (typeof window !== "undefined" ? window.location.search : ""),
        );
      return <Navigate to={resumePath} replace />;
    }

    const path = location.pathname.replace(/\/+$/, "") || "/";
    const normalizedDefault = defaultSignedInPath.replace(/\/+$/, "");
    const isAppShellEntry =
      path === "/" ||
      path === `/${ROUTESWEB.APP.DASHBOARD}` ||
      path === normalizedDefault;

    // Shell while Genius OTP pending → always resume (never blank — blocks user).
    // Auth escape paths are excluded above so login always works after failures.
    if (isAppShellEntry && !onAuthEscape) {
      const geniusResumePath = resolvePendingGeniusResumePath();
      if (geniusResumePath) {
        markGeniusShellResumeInThisTab();
        return <Navigate to={geniusResumePath} replace />;
      }
    }
  }

  if (requireAuth) {
    if (!isAuthenticated) {
      if (isGeniusDeeplink(window.location.href)) {
        const search =
          location.search ||
          (typeof window !== "undefined" ? window.location.search : "");
        return <Navigate to={buildGeniusDeeplinkPath(search)} replace />;
      }

      // Do not force Genius resume when the user is trying to reach login/auth.
      if (!onAuthEscape) {
        const pendingGeniusPath = resolvePendingGeniusResumePath();
        if (pendingGeniusPath) {
          markGeniusShellResumeInThisTab();
          return <Navigate to={pendingGeniusPath} replace />;
        }
      }

      const searchParams = new URLSearchParams(location.search);
      const isFromDeepLink =
        searchParams.get("isFromDeepLinking") === "true" ||
        searchParams.get("isFromSurgeVideos") === "true" ||
        searchParams.has("module");

      return (
        <Navigate
          to={`/${ROUTESWEB.AUTH.LOGIN}`}
          state={isFromDeepLink ? { from: location } : {}}
          replace
        />
      );
    }
  } else {
    if (isAuthenticated) {
      const isSignupPath =
        location.pathname === `/${ROUTESWEB.AUTH.SIGNUP}` ||
        location.pathname.endsWith(`/${ROUTESWEB.AUTH.SIGNUP}`);
      const state = location.state as {
        from?: Location;
        addProfileForLoggedInUser?: boolean;
        isUpdateFlow?: boolean;
      } | null;
      if (
        isSignupPath &&
        (state?.addProfileForLoggedInUser || state?.isUpdateFlow)
      ) {
        return <Outlet />;
      }

      // Auth escape already abandoned Genius above — do not bounce back to Genius.
      if (!onAuthEscape) {
        const pendingGeniusPath = resolvePendingGeniusResumePath();
        if (pendingGeniusPath) {
          return <Navigate to={pendingGeniusPath} replace />;
        }
      }

      const fromState = (location.state as { from?: Location })?.from;
      const target = fromState
        ? `${fromState.pathname}${fromState.search || ""}`
        : defaultSignedInPath;
      return <Navigate to={target} replace />;
    }
  }

  return <Outlet />;
};
